const DietMatrice = (json: IDietMatrice): IDietMatriceModel => {
	const { diet_id, meal_type, size_id, updated_at, created_at, ...rest } = json

	return {
		...rest,
		dietID: diet_id,
		mealType: meal_type,
		sizeID: size_id,
		updatedAt: updated_at ? new Date(updated_at) : null,
		createdAt: created_at ? new Date(created_at) : null,
	}
}

const mapPrice = (price: IDietPrice) => ({
	kcal: price.kcal,
	price: typeof price.price === 'number' ? price.price / 100 : null,
})

export const Diet = (json: IDiet): IDietModel => {
	const fromJSON = () => {
		const {
			name,
			name_short,
			description,
			description_short,
			diet_type,
			diet_group,
			product_line,
			active_to_order_from,
			active_to_order_to,
			prices,
			carbs_percent,
			protein_percent,
			fat_percent,
			meal_types,
			media,
			filter_tags,
			marketing_tags,
			allergen_exclusions,
			dietary_tag_exclusions,
			matrices,
			...rest
		} = json

		return {
			name: safeLangContent(name),
			nameShort: safeLangContent(name_short),
			description: safeLangContent(description),
			descriptionShort: safeLangContent(description_short),
			dietType: diet_type,
			dietGroup: diet_group,
			productLine: product_line ?? EProductLine.FOODIFY,
			activeToOrderFrom: active_to_order_from ? new Date(active_to_order_from) : null,
			activeToOrderTo: active_to_order_to ? new Date(active_to_order_to) : null,
			prices: safeMap(prices, mapPrice, []),
			carbsPercent: carbs_percent,
			proteinsPercent: protein_percent,
			fatsPercent: fat_percent,
			mealTypes: safeMap(meal_types, mealType => DietMealType(mealType), []),
			media: safeMap(media, m => Media(m), []),
			filterTags: safeMap(filter_tags, f => DietFilterTag(f), []),
			marketingTags: safeMap(marketing_tags, m => DietMarketingTag(m), []),
			allergenExclusions: safeMap(allergen_exclusions, DietAllergen, []),
			dietaryTagExclusions: safeMap(dietary_tag_exclusions, e => DietDietaryTag(e), []),
			matrices: safeMap(matrices, m => DietMatrice(m), []),
			...rest,
		}
	}

	return { ...fromJSON() }
}

export const dietToJSON = (obj: Partial<IDietModel>): Partial<IDiet> => {
	const { ...rest } = obj
	return {
		...rest,
	}
}

export const DietCalories = (json: IDietCalories): IDietCaloriesModel => {
	const { id, value } = json

	return {
		id: id.toString(),
		value,
	}
}

/**
 * Payloads
 */
export const DietNumberOfMealsToPayload = (model: IDietNumberOfMeals): IDietNumberOfMealsPayload[] => {
	const meals: IDietNumberOfMealsPayload[] = []

	Object.entries(model).forEach(([key, value]) => {
		meals.push({ meal_type: key as EMealType, meal_count: value.value ?? 0 })
	})

	return meals
}

const buildOrderDateRange = (
	isStatusActive: boolean,
	isDateRange: boolean,
	activeToOrderFrom: unknown,
	activeToOrderTo: unknown,
): { from: Date | undefined; to: Date | undefined } => {
	const to = isDateRange && activeToOrderTo ? new Date(activeToOrderTo as string) : undefined
	const from = !isStatusActive && isDateRange && activeToOrderFrom ? new Date(activeToOrderFrom as string) : undefined
	if (to) to.setHours(23, 59, 59, 999)
	if (from) from.setHours(23, 59, 59, 999)
	return { from, to }
}

const buildMatricesPayload = (
	numberOfMeals: IDietPayloadUtils['numberOfMeals'],
	calories: IDietPayloadUtils['calories'],
	eventValues: IFormSubmitEvent<IDietFormBaseModel>['values'],
): IDietMatricesPayload[] =>
	Object.entries(numberOfMeals).flatMap(([key, value]) => {
		if (value.value === 0) return []

		return calories.flatMap(calorie => {
			const isDisabled = eventValues[`matrice[disabled][${key}][${calorie}]`]
			const size = eventValues[`matrice[size][${key}][${calorie}]`]

			if (isDisabled) return []

			// @ts-ignore value of event values is number, but type is string
			return { kcal: calorie, size_id: size ?? null, meal_type: key as EMealType } as IDietMatricesPayload
		})
	})

export const DietSharedToPayload = (
	event: IFormSubmitEvent<IDietFormBaseModel>,
	utils: IDietPayloadUtils,
): IDietSharedPayload => {
	const { allergens, dietaryTags, filterTags, marketingTags, numberOfMeals, isStatusActive, calories } = utils

	const {
		activeToOrderFrom,
		activeToOrderTo,
		proteinPercent,
		carbsPercent,
		fatPercent,
		order,
		isDateRange,
		productLine,
	} = event.values

	const status = isStatusActive ? EDietStatus.ACTIVE : EDietStatus.INACTIVE
	const { from: _activeToOrderFrom, to: _activeToOrderTo } = buildOrderDateRange(
		isStatusActive,
		isDateRange,
		activeToOrderFrom,
		activeToOrderTo,
	)

	const matrices = buildMatricesPayload(numberOfMeals, calories, event.values)

	return {
		status,
		order,
		active_to_order_from: _activeToOrderFrom,
		active_to_order_to: _activeToOrderTo,
		protein_percent: proteinPercent,
		carbs_percent: carbsPercent,
		fat_percent: fatPercent,
		kcals: calories,
		meal_types: DietNumberOfMealsToPayload(numberOfMeals),
		filter_tags: filterTags?.map(f => f.value) ?? [],
		marketing_tags: marketingTags?.map(m => m.value) ?? [],
		allergen_exclusions: allergens ?? [],
		dietary_tag_exclusions: dietaryTags ?? [],
		matrices: matrices ?? [],
		product_line: productLine ?? EProductLine.FOODIFY,
	}
}

export const DietUpdateToPayload = (
	updated: IDietModel,
	event: IFormSubmitEvent<IDietFormBaseModel>,
	utils: IDietPayloadUtils,
): IDietUpdatePayload => {
	const { name, nameShort, description, descriptionShort, productLine } = event.values

	const shared = DietSharedToPayload(event, utils)

	let _description: ReturnType<typeof LangContentToJson> | ILangContent[] | undefined
	if (updated.description) _description = LangContentToJson(updated.description, description)
	else if (description) _description = generateLangContentArray(description)

	let _descriptionShort: ReturnType<typeof LangContentToJson> | ILangContent[] | undefined
	if (updated.descriptionShort) _descriptionShort = LangContentToJson(updated.descriptionShort, descriptionShort)
	else if (descriptionShort) _descriptionShort = generateLangContentArray(descriptionShort)

	return {
		id: updated.id,
		name: LangContentToJson(updated.name, name),
		name_short: LangContentToJson(updated.nameShort, nameShort),
		description: _description,
		description_short: _descriptionShort,
		diet_type: updated.dietType,
		...shared,
		product_line: productLine ?? updated.productLine,
	}
}

export const DietCreateToPayload = (
	event: IFormSubmitEvent<IDietFormBaseModel>,
	utils: IDietPayloadUtils,
): IDietCreatePayload => {
	const { name, nameShort, description, descriptionShort, productLine } = event.values

	const shared = DietSharedToPayload(event, utils)

	const _description = description ? generateLangContentArray(description) : undefined
	const _descriptionShort = descriptionShort ? generateLangContentArray(descriptionShort) : undefined

	return {
		name: generateLangContentArray(name),
		name_short: generateLangContentArray(nameShort),
		description: _description,
		description_short: _descriptionShort,
		diet_type: EDietType.STANDARD,
		...shared,
		product_line: productLine ?? EProductLine.FOODIFY,
	}
}
